import type { Condition, FilterNode } from '@servicetim/shared';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@servicetim/ui/components';
import { cn } from '@servicetim/ui/lib';
import { Plus, Trash2 } from 'lucide-react';
import type { FieldCatalog } from '../../projects/catalog/field-catalog';
import { FieldConditionRow } from '../../projects/conditions/field-condition-row';
import { removeAt, replaceAt } from '../../projects/lib/list-utils';
import { SimpleSelect } from '../../projects/ui/simple-select';

export interface FilterBuilderProps {
  value: FilterNode | undefined;
  /** `undefined` removes the node (root: no filter). */
  onChange: (next: FilterNode | undefined) => void;
  catalog: FieldCatalog;
  idPrefix: string;
  depth?: number;
}

type GroupKind = 'and' | 'or' | 'not';
const GROUP_OPTIONS = [
  { value: 'and', label: 'All of (and)' },
  { value: 'or', label: 'Any of (or)' },
  { value: 'not', label: 'None of (not)' },
];

const isLeaf = (node: FilterNode): node is Condition => 'field' in node;
const groupKind = (node: FilterNode): GroupKind | null =>
  'and' in node ? 'and' : 'or' in node ? 'or' : 'not' in node ? 'not' : null;
const children = (node: FilterNode): FilterNode[] =>
  'and' in node ? node.and : 'or' in node ? node.or : 'not' in node ? [node.not] : [];
const makeGroup = (kind: GroupKind, items: FilterNode[]): FilterNode | undefined => {
  if (items.length === 0) return undefined;
  if (kind === 'and') return { and: items };
  if (kind === 'or') return { or: items };
  return { not: items.length === 1 ? (items[0] as FilterNode) : { and: items } };
};

export const newLeaf = (catalog: FieldCatalog): FilterNode => ({
  field: catalog.fields[0]?.key ?? 'status_id',
  op: 'eq',
});

/**
 * Recursive `FilterNode` editor: and / or / not groups with `{ field, op, value }` leaves bound
 * to the project's field catalog. Empty groups collapse away so the output always satisfies
 * `FilterNodeSchema` (groups need at least one child).
 */
export function FilterBuilder({
  value,
  onChange,
  catalog,
  idPrefix,
  depth = 0,
}: FilterBuilderProps) {
  if (value === undefined) {
    return (
      <div className="flex flex-wrap items-center gap-3 rounded-md border border-border border-dashed p-3">
        <span className="text-fg-muted text-sm">No filter: every ticket you can see.</span>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => onChange({ and: [newLeaf(catalog)] })}
        >
          <Plus />
          Add condition
        </Button>
      </div>
    );
  }
  const kind = groupKind(value);
  if (kind === null || isLeaf(value)) {
    return (
      <FieldConditionRow
        idPrefix={idPrefix}
        value={value as Condition}
        catalog={catalog}
        onChange={(next) =>
          onChange(next.value === undefined ? { field: next.field, op: next.op } : next)
        }
        onRemove={() => onChange(undefined)}
      />
    );
  }
  const items = children(value);
  const setItems = (next: FilterNode[]) => onChange(makeGroup(kind, next));

  return (
    <div
      className={cn(
        'space-y-2 rounded-md border p-3',
        depth === 0 ? 'border-border bg-surface-raised' : 'border-border bg-surface-sunken',
      )}
      data-slot="filter-group"
    >
      <div className="flex flex-wrap items-center gap-2">
        <SimpleSelect
          aria-label="Group operator"
          size="sm"
          className="w-40"
          value={kind}
          options={GROUP_OPTIONS}
          onChange={(next) =>
            next && next !== kind && onChange(makeGroup(next as GroupKind, items))
          }
        />
        <span className="text-fg-muted text-xs">
          {kind === 'and'
            ? 'every condition must match'
            : kind === 'or'
              ? 'at least one must match'
              : 'none may match'}
        </span>
        {depth > 0 ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="ml-auto"
            aria-label="Remove group"
            onClick={() => onChange(undefined)}
          >
            <Trash2 />
          </Button>
        ) : null}
      </div>
      <ul className="space-y-2">
        {items.map((item, index) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: rows carry no identity; their position is the key
          <li key={`${idPrefix}-${index}-${groupKind(item) ?? 'leaf'}`}>
            <FilterBuilder
              value={item}
              onChange={(next) =>
                setItems(
                  next === undefined ? removeAt(items, index) : replaceAt(items, index, next),
                )
              }
              catalog={catalog}
              idPrefix={`${idPrefix}-${index}`}
              depth={depth + 1}
            />
          </li>
        ))}
      </ul>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button type="button" variant="outline" size="sm" disabled={items.length >= 50}>
            <Plus />
            Add
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          <DropdownMenuItem onSelect={() => setItems([...items, newLeaf(catalog)])}>
            Condition
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => setItems([...items, { and: [newLeaf(catalog)] }])}>
            Group: all of
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => setItems([...items, { or: [newLeaf(catalog)] }])}>
            Group: any of
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => setItems([...items, { not: newLeaf(catalog) }])}>
            Group: none of
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
